import { AbstractComponent } from "appworks/components/abstract-component";
import { Layers } from "appworks/graphics/layers/layers";
import { DualPosition } from "appworks/graphics/pixi/dual-position";
import { PIXIElement } from "appworks/graphics/pixi/group";
import { Position } from "appworks/graphics/pixi/position";
import { Text } from "appworks/graphics/pixi/text";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { scaleIn, scaleOut } from "appworks/utils/animation/scale";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { TweenContract } from "appworks/utils/contracts/tween-contract";
import { Easing, Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { ApplyFowlPlayTextStyling } from "util/fowl-text-style";

export class FowlBonusTransitionComponent extends AbstractComponent {
    protected readonly bounds = new DualPosition(new Position(0, 0, 1920, 1080), new Position(0, 0, 1080, 1920));

    protected readonly panDuration = 1500;
    protected readonly panDelay = 300;
    protected readonly panEasing = Easing.Quadratic.Out;

    protected targetLayers = [
        // the layers which will move up/down as part of the panning movement
        gameLayers.EggPickPaytable,
        gameLayers.BonusUI,
        gameLayers.EggPickBonus,
        gameLayers.MenuButton,
        gameLayers.Collect,
        gameLayers.SpinCounter,
        gameLayers.Logo,
        gameLayers.SpinButton,
        gameLayers.CoinHopper,
        gameLayers.SlingoLadder,
        gameLayers.SymbolAnimations,
        gameLayers.MatrixForeground,
        gameLayers.MatrixContent,
        gameLayers.MatrixBackground,
        gameLayers.TicketMatrix,
        gameLayers.TicketMatrixBacking,
        gameLayers.BackgroundIntermittentAnimation,
        gameLayers.BackgroundPersistentAnimation,
        gameLayers.Background,
    ];
    protected startPositions: Map<PIXIElement, { landscapeY: number; portraitY: number }> = new Map();

    protected transitionActive: boolean = false;
    protected bonusGraphic: PIXIElement;
    protected layer = gameLayers.BonusTransition;

    public init(): void {
        this.targetLayers.forEach(layer => {
            this.startPositions.set(layer.container, { landscapeY: layer.container.landscape.y, portraitY: layer.container.portrait.y });
            layer.onSceneEnter.add(() => {
                this.startPositions.set(layer.container, { landscapeY: layer.container.landscape.y, portraitY: layer.container.portrait.y });

                if (this.transitionActive) {
                    // if we're mid-transition, offset the scene container back down on scene change, as it'll reset
                    layer.container.landscape.y += this.bounds.landscape.height;
                    layer.container.portrait.y += this.bounds.portrait.height;
                }
            });
        });

        this.hideBonusGraphics();

        for (const key in this.layer.scene.default.contents) {
            const element = this.layer.get(key);
            if (element instanceof Text) {
                ApplyFowlPlayTextStyling(element, 4);
            }
        }
    }

    public in(bonusType: string): Contract {
        this.transitionActive = true;

        const transitionSpine = this.layer.getSpine("bonus_transition");

        this.bonusGraphic = this.getBonusGraphicForBonusType(bonusType);
        this.bonusGraphic.visible = true;
        this.bonusGraphic.landscape.scale.set(0);
        this.bonusGraphic.portrait.scale.set(0);

        const moveTargets = this.targetLayers.map(layer => layer.container);

        // These are a special case because they should move, but they're in the non-moving betbar layer
        const stakeButtons = [gameLayers.BetBar.getButton("ui_stake_menu"), gameLayers.BetBar.getButton("ui_stake_menu_p")].filter(btn => Boolean(btn));
        stakeButtons.forEach(btn => this.startPositions.set(btn, { landscapeY: btn.landscape.y, portraitY: btn.portrait.y }));
        moveTargets.push(...stakeButtons);

        return new Sequence([
            () => Contract.wrap(() => this.toggleBgTree(true)),
            () =>
                new Parallel([
                    ...moveTargets.map(target => () =>
                        new Parallel([
                            () =>
                                TweenContract.wrapTween(
                                    new Tween(target.landscape)
                                        .to({ y: this.startPositions.get(target).landscapeY + this.bounds.landscape.height }, this.panDuration)
                                        .easing(this.panEasing)
                                        .delay(this.panDelay)
                                ),
                            () =>
                                TweenContract.wrapTween(
                                    new Tween(target.portrait)
                                        .to({ y: this.startPositions.get(target).portraitY + this.bounds.portrait.height }, this.panDuration)
                                        .easing(this.panEasing)
                                        .delay(this.panDelay)
                                ),
                        ])
                    ),
                    () => transitionSpine.playOnce(bonusType + "_in"),
                    () => Contract.getDelayedContract(500, () => scaleIn(this.bonusGraphic, 750, Easing.Back.Out)),
                ]),
        ]);
    }

    public out(bonusType: string): Contract {
        this.transitionActive = false;

        const transitionSpine = this.layer.getSpine("bonus_transition");

        const moveTargets = [
            ...this.targetLayers.map(layer => layer.container),
            gameLayers.BetBar.getButton("ui_stake_menu"),
            gameLayers.BetBar.getButton("ui_stake_menu_p"),
        ].filter(tgt => Boolean(tgt));

        return new Sequence([
            () =>
                new Parallel([
                    ...moveTargets.map(target => () =>
                        new Parallel([
                            () =>
                                TweenContract.wrapTween(
                                    new Tween(target.landscape).to({ y: this.startPositions.get(target).landscapeY }, this.panDuration).easing(this.panEasing)
                                ),
                            () =>
                                TweenContract.wrapTween(
                                    new Tween(target.portrait).to({ y: this.startPositions.get(target).portraitY }, this.panDuration).easing(this.panEasing)
                                ),
                        ])
                    ),
                    () =>
                        new Sequence([
                            () => transitionSpine.playOnce(bonusType + "_loop"),
                            () => new Parallel([() => transitionSpine.playOnce(bonusType + "_out"), () => scaleOut(this.bonusGraphic, 300, Easing.Cubic.In)]),
                        ]),
                ]),
            () => Contract.wrap(() => this.toggleBgTree(false)),
        ]);
    }

    // The bg has a tree spine anim playing, but there's no top to it, so we switch to a static for the transition
    protected toggleBgTree(showStatic: boolean) {
        const staticTree = gameLayers.BackgroundPersistentAnimation.getSprite("tree_static");
        const spineTree = gameLayers.BackgroundPersistentAnimation.getSpine("tree");

        if (staticTree) staticTree.visible = showStatic;
        if (spineTree) spineTree.visible = !showStatic;
    }

    protected getBonusGraphicForBonusType(bonusType: string): PIXIElement {
        if (bonusType.includes("egg_pick")) bonusType = "egg_pick";

        const langSuffix = Services.get(TranslationsService).playingInEnglish() ? "_en" : "_i18n";
        return this.layer.get(bonusType + langSuffix) as PIXIElement;
    }

    protected hideBonusGraphics() {
        this.layer.scene.default.contents.egg_pick_en.visible = false;
        this.layer.scene.default.contents.egg_pick_i18n.visible = false;
        this.layer.scene.default.contents.fowl_play_en.visible = false;
        this.layer.scene.default.contents.fowl_play_i18n.visible = false;
        this.layer.scene.default.contents.scatters_en.visible = false;
        this.layer.scene.default.contents.scatters_i18n.visible = false;
    }
}
